// Pure helpers for the Partner Routing & Packing screen.
// All transformations mirror cp-routing.vue's helpers verbatim — the goal is
// 1:1 visual + behavioral parity with the web.

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// % delivered (OFD) for a LOCALITY card.
export const ofdPercent = (card) => {
  const ofd = safeNum(card?.ofd_tps);
  const total = safeNum(card?.tp_count);
  if (ofd == null || total == null || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((ofd / total) * 100)));
};

// % packed for a LOCALITY card.
export const packedPercent = (card) => {
  const packed = safeNum(card?.packed_tps);
  const total = safeNum(card?.tp_count);
  if (packed == null || total == null || total <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((packed / total) * 100)));
};

// Packing-status pill (CRATE tab only).
export const packingPill = (card) => {
  const packed = safeNum(card?.packed_tps);
  const total = safeNum(card?.tp_count);
  if (packed == null || total == null || total < 0) return null;
  if (total > 0 && packed >= total) {
    return { label: "FULLY PACKED", state: "packedFull" };
  }
  if (packed <= 0) return { label: "UNPACKED", state: "unpacked" };
  return {
    label: `PARTIALLY PACKED ${packed}/${total}`,
    state: "packedPartial",
  };
};

// DB-pill state for a (card, db) pair.
// - default: DB not in active_dbs.
// - active: DB in active_dbs and exactly one DB is active on this card.
// - activeMulti: DB in active_dbs and >1 DBs are active on this card.
export const dbPillState = (card, cpDbId) => {
  const active = Array.isArray(card?.active_dbs)
    ? card.active_dbs.filter(Boolean)
    : [];
  const isActive = active.some((e) => String(e?.cp_db_id) === String(cpDbId));
  if (!isActive) return "default";
  return active.length > 1 ? "activeMulti" : "active";
};

// TP count badge for a DB on this card; web hides 0.
export const dbPillCount = (card, cpDbId) => {
  const active = Array.isArray(card?.active_dbs)
    ? card.active_dbs.filter(Boolean)
    : [];
  const found = active.find((e) => e?.cp_db_id === cpDbId);
  return Number(found?.tp_count) || 0;
};

// Whether a card has the MISROUTE pseudo-DB active. CUSTOMER tab uses this
// to colour the OUT_OF_AREA pill orange when already misrouted.
export const isMisrouteActive = (card) => {
  const active = Array.isArray(card?.active_dbs) ? card.active_dbs : [];
  return active.some((e) => String(e?.cp_db_id) === "MISROUTE");
};

// Apply search + sort to the raw list returned by the API.
export const applyFilterAndSort = (
  list = [],
  { searchTerm = "", selectedSort = null, sortDescending = true } = {},
) => {
  const term = String(searchTerm || "")
    .trim()
    .toLowerCase();

  let result = term
    ? list.filter((card) =>
        String(card?.search_key || "")
          .toLowerCase()
          .includes(term),
      )
    : list.slice();

  if (selectedSort === "TP") {
    result = result
      .slice()
      .sort((a, b) => Number(b?.tp_count || 0) - Number(a?.tp_count || 0));
  } else if (selectedSort === "LARGE_CRATES") {
    result = result
      .slice()
      .sort(
        (a, b) =>
          Number(b?.connecting_large_crate_count || 0) -
          Number(a?.connecting_large_crate_count || 0),
      );
  } else if (selectedSort === "SMALL_CRATES") {
    result = result
      .slice()
      .sort(
        (a, b) =>
          Number(b?.connecting_small_crate_count || 0) -
          Number(a?.connecting_small_crate_count || 0),
      );
  }

  if (selectedSort && sortDescending === false) {
    result = result.slice().reverse();
  }

  return result;
};

// Shared "Ready DB" derivation. A DB is ready when every assigned customer
// is on a fully-packed crate (the executive has tapped Pack Visible Items
// for each of their crates). Used by PackingDbListScreen.
export const deriveReadyDbs = (packingDbs = []) => {
  const active = packingDbs.filter((d) => Number(d?.customers) > 0);
  const ready = active.filter(
    (d) => Number(d?.packed) === Number(d?.customers),
  ).length;
  return { active, ready };
};

/** D27:C — label for packing trip chip when order is already OFD. */
export const formatPackingTripLabel = (tripId) => {
  if (tripId == null || tripId === "") return "OFD";
  const tid = String(tripId);
  return tid.length > 10 ? `Trip · …${tid.slice(-6)}` : `Trip · ${tid}`;
};

/** Unique trip labels from packing items with is_ofd + trip_id. */
export const packingTripLabelsFromItems = (items = []) => {
  const seen = new Set();
  const labels = [];
  for (const it of items) {
    if (!it?.is_ofd) continue;
    const key = it.trip_id != null ? String(it.trip_id) : "__ofd__";
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(formatPackingTripLabel(it.trip_id));
  }
  return labels;
};
